import { Ionicons } from "@expo/vector-icons";
import type { NativeStackNavigationProp } from "@react-navigation/native-stack";
import _ from "lodash";
import { AlertDialog, Box, Button, Flex, HStack, Pressable, Text as NBText, useTheme, VStack } from "native-base";
import React from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";

import ProgressBar from "../commons/ProgressBar";
import { Routes } from "../constants";
import { FeatureFlag, isFeatureFlagActive } from "../helpers/featureFlags";
import {
  formatMealType,
  formatMomentAsBackendFormatDateString,
  formatNumberAsWholeNumber,
} from "../helpers/generalHelpers";
import {
  doesOrganisationHaveManagedPlanning,
  FeatureFlag as UserHelperFeatureFlag,
  getOrganisation,
  isFeatureFlagEnabled,
  isSimpleOrg,
} from "../helpers/userHelpers";
import type { RootStackParamList } from "../navigation/NavigationStackParams";
import backendApi from "../services/backendApi";
import type {
  CalendarDay,
  CalendarItem,
  CalendarItemStatusEnum,
  Meal,
  MealMomentEnum,
  MealSlotSpecification,
  SimpleRecipeMeal,
  SingleFoodMeal,
} from "../services/backendTypes";
import logger from "../services/logger";
import { currentDayInPlannerSelector } from "../slices/plannerSlice";
import { userSelector, viewAsUserSelector } from "../slices/userSlice";
import CalendarItemComponent from "./PlannerItem";

const {
  usePlannerCalendarItemDestroyMutation,
  usePlannerCalendarItemPartialUpdateMutation,
  useFoodIngredientPartialUpdateMutation,
  usePlannerAutoPlanDayCreateMutation,
} = backendApi;

type Props = {
  title?: string;
  color?: string;
  actualValue: number;
  targetValue: number;
  currentCalendarDay: CalendarDay;
  calendarItems?: CalendarItem[];
  mealSlotSpecification: MealSlotSpecification;
  navigation: NativeStackNavigationProp<RootStackParamList, Routes.DiaryViewScreen>;
  onPressPlannedStatus: (arg0: number, arg1?: string) => void;
  refetchCalendarDays: () => void;
  onPressAddMoreComponent: () => void;
  onPressPlanNewMeal: () => void;
  onPressMenu: () => void;
  showTarget?: boolean;
  onPressCopyMealFromCard?: (mealMoment: MealMomentEnum) => void;
};

const MealSlotInPlanner = ({
  title,
  color,
  actualValue,
  targetValue,
  currentCalendarDay,
  calendarItems,
  mealSlotSpecification,
  navigation,
  onPressPlannedStatus,
  refetchCalendarDays,
  onPressAddMoreComponent,
  onPressPlanNewMeal,
  onPressMenu,
  showTarget = true,
  onPressCopyMealFromCard,
}: Props): JSX.Element => {
  const { t } = useTranslation();

  const theme = useTheme();

  const [autoPlanDay, { isLoading: isLoadingAutoPlanDay }] = usePlannerAutoPlanDayCreateMutation();
  const currentDayInPlanner = useSelector(currentDayInPlannerSelector);
  const viewAsUser = useSelector(viewAsUserSelector);
  const realUser = useSelector(userSelector);
  const user = viewAsUser || realUser;

  const organisation = user ? getOrganisation(user) : null;
  const productLoggingIsDisabled = isFeatureFlagEnabled(
    organisation || null,
    UserHelperFeatureFlag.DisableProductLogging
  );
  const shouldHideMacros = isFeatureFlagActive(user, FeatureFlag.FF_DONT_SHOW_MACROS);

  const [updateCalendarItem, { isLoading: isLoadingUpdateCalendarItem }] =
    usePlannerCalendarItemPartialUpdateMutation();
  const [updateSingleFoodMealIngredient, { isLoading: isLoadingUpdateSingleFoodMealIngredient }] =
    useFoodIngredientPartialUpdateMutation();

  const [calendarItemToDeleteId, setCalendarItemToDeleteId] = React.useState<number | undefined>(undefined);
  const [isOpenDeleteMenu, setIsOpenDeleteMenu] = React.useState(false);

  const createPlannerItemFromCalendarItem = ({
    content_type: contentType,
    meal,
    meal_slot: mealSlot,
    status,
    id,
  }: CalendarItem): JSX.Element => {
    const onPressCalendarItemPlannedStatus = async (): Promise<void> => {
      if (!id) {
        throw new Error("id is undefined");
      }
      if (!meal.id) {
        throw new Error("meal.id is undefined");
      }

      onPressPlannedStatus(meal.id, title);

      const newStatus: CalendarItemStatusEnum = status === "PLANNED" ? "EATEN" : "PLANNED";
      await updateCalendarItem({ id, patchedCalendarItemRequest: { status: newStatus } }).unwrap();
      return refetchCalendarDays();
    };

    const onSwapCalendarItem = async (): Promise<void> => {
      // Note:  We only swap recipemeals
      if (contentType !== "recipemeal") {
        return;
      }

      if (!user) {
        throw new Error("user is undefined");
      }

      await autoPlanDay({
        autoPlannerRequest: {
          date: formatMomentAsBackendFormatDateString(currentDayInPlanner),
          user: user.id,
          meal_slot_ids: [mealSlot],
        },
      });
    };

    const onPressCalendarItem = (): void => {
      if (contentType === "recipemeal") {
        const recipeMealEditable = !isFeatureFlagEnabled(
          organisation || null,
          UserHelperFeatureFlag.DisableProductLogging
        );

        navigation.push(Routes.AddRecipeTemplateStack, {
          // NOTE: We are probably defining the screen types wrong
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          screen: Routes.RecipeDetailScreen,
          params: {
            recipeMeal: meal,
            mealSlotSpecification,
            mealType: "RecipeMeal",
            viewOnly: true,
            editable: recipeMealEditable,
            portionsEditable: true,
          },
        });
      } else if (contentType === "singlefoodmeal") {
        if (!(meal as SingleFoodMeal).ingredient || !(meal as SingleFoodMeal).ingredient.id) {
          throw new Error("meal.ingredient.id is undefined");
        }

        navigation.push(Routes.AddProductStack, {
          screen: Routes.AddIngredientScreen,
          params: {
            onChoose: async (product, suggestedServing, quantity) => {
              await updateSingleFoodMealIngredient({
                // We explicitly check that id is not undefined above
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                id: (meal as SingleFoodMeal).ingredient.id,
                patchedIngredientRequest: {
                  suggested_serving: suggestedServing,
                  quantity,
                },
              }).unwrap();
              navigation.pop(1);
              refetchCalendarDays();
            },
            ingredient: (meal as SingleFoodMeal).ingredient,
          },
        });
      } else if (contentType === "simplerecipemeal") {
        const simpleRecipeMeal = meal as SimpleRecipeMeal;

        navigation.push(Routes.SimpleRecipeDetailScreen, {
          simpleRecipeMeal,
          mealSlotSpecification,
          currentCalendarDay,
          // Don't pass onSelect or viewOnly for existing meals - this allows portion editing
        });
      }
    };

    const handleListAlternativesPress = (): void => {
      if (user && isSimpleOrg(user)) {
        navigation.push(Routes.SimpleRecipeSearchScreen, {
          mealSlotSpecification,
          currentCalendarDay,
          calendarItemToReplace: id,
        });
      } else {
        navigation.push(Routes.RecipeSearchScreen, {
          mealSlotSpecification,
          currentCalendarDay,
          calendarItemToReplace: id,
        });
      }
    };

    const onLongPressCalendarItem = (): void => {
      logger.debug("onLongPressCalendarItem");
      setIsOpenDeleteMenu(true);

      setCalendarItemToDeleteId(id);
    };

    const onPressCopyMealFromCardHandler = (): void => {
      if (mealSlotSpecification.meal_moment && onPressCopyMealFromCard) {
        onPressCopyMealFromCard(mealSlotSpecification.meal_moment);
      }
    };

    // TODO: Memoize this (but it is difficult to use the useCallback
    // hook because we are inside a function (not a react component))
    return (
      <CalendarItemComponent
        key={id}
        meal={meal}
        onPress={onPressCalendarItem}
        onSwap={onSwapCalendarItem}
        onLongPress={onLongPressCalendarItem}
        onListAlternativesPress={() => handleListAlternativesPress()}
        isLoading={isLoadingAutoPlanDay || isLoadingUpdateCalendarItem}
        // eslint-disable-next-line @typescript-eslint/no-misused-promises
        onPressCalendarItemPlannedStatus={onPressCalendarItemPlannedStatus}
        status={status || "PLANNED"}
        contentType={contentType}
        editable={!productLoggingIsDisabled}
        mealMoment={mealSlotSpecification.meal_moment}
        onPressCopyMeal={contentType === "recipemeal" ? onPressCopyMealFromCardHandler : undefined}
      />
    );
  };
  const [deleteCalendarItemOnBackend, { isLoading: isLoadingDeleteCalendarItemOnBackend }] =
    usePlannerCalendarItemDestroyMutation();
  const deleteCalendarItem = async (calendarItemId: number): Promise<void> => {
    if (!calendarItemId) {
      throw new Error("calendarItemId is undefined");
    }

    if (calendarItemId) {
      await deleteCalendarItemOnBackend({ id: calendarItemId }).unwrap();
      setIsOpenDeleteMenu(false);
      refetchCalendarDays();
    }
  };

  const onClose = (): void => setIsOpenDeleteMenu(false);
  const cancelRef = React.useRef(null);
  const deleteMealComponent = (
    <AlertDialog
      leastDestructiveRef={cancelRef}
      isOpen={isOpenDeleteMenu}
      onClose={onClose}
      testID={"delete-planned-meal-modal"}
    >
      <AlertDialog.Content bg="primary.10" borderRadius={theme.borderRadii.modal.default}>
        <AlertDialog.Header bg="primary.10" color="function.50" fontSize="lg" fontWeight="bold" borderBottomWidth={0}>
          {t("planner.delete_meal_confirm_button_text")}
        </AlertDialog.Header>
        <AlertDialog.Footer bg="primary.10" borderTopWidth={0}>
          <Button.Group space={2}>
            <Button
              variant="outline"
              borderColor="function.20"
              _text={{ color: "function.30" }}
              _pressed={{ bg: "function.10" }}
              borderRadius={theme.borderRadii.button.default}
              shadow="1"
              onPress={onClose}
              ref={cancelRef}
            >
              {t("general.cancel")}
            </Button>
            <Button
              isLoading={isLoadingDeleteCalendarItemOnBackend}
              bg="error.500"
              _text={{ color: "white" }}
              _pressed={{ bg: "error.600" }}
              borderRadius={theme.borderRadii.button.default}
              shadow="2"
              // eslint-disable-next-line @typescript-eslint/no-misused-promises
              onPress={async () => {
                if (!calendarItemToDeleteId) throw new Error("calendarItemToDeleteId is undefined");
                await deleteCalendarItem(calendarItemToDeleteId);
              }}
              testID="delete-planned-meal-modal-confirm-button"
            >
              {t("general.delete")}
            </Button>
          </Button.Group>
        </AlertDialog.Footer>
      </AlertDialog.Content>
    </AlertDialog>
  );

  const disablePlanMealAction = !currentCalendarDay;

  const planMealComponent = (
    <Pressable onPress={onPressPlanNewMeal} isDisabled={disablePlanMealAction}>
      {({ isPressed }) => (
        <Box
          bg={isPressed ? "primary.30" : "transparent"}
          borderRadius={theme.borderRadii.card.default}
          px={3}
          py={3}
          minHeight={"60px"}
          justifyContent="center"
          nativeID={`planMealComponent-${mealSlotSpecification.meal_moment}`}
        >
          <HStack justifyContent="space-between" alignItems="center">
            <NBText fontFamily={theme.fonts.serif} fontStyle="italic" fontSize="24px" color="function.50" flex={1}>
              {mealSlotSpecification.meal_moment ? formatMealType(mealSlotSpecification.meal_moment, t) : ""}
            </NBText>
            <Box size="36px" borderRadius="full" justifyContent="center" alignItems="center">
              <Ionicons name="add" size={20} color={theme.colors.function[50]} />
            </Box>
          </HStack>
        </Box>
      )}
    </Pressable>
  );

  // TODO: memo() or useCallback()?
  const renderItemFunction = (index: number, calendarItem: CalendarItem): JSX.Element =>
    createPlannerItemFromCalendarItem(calendarItem);

  // TODO: We should consider sorting by created_at but this property does not exist on the model
  const headerComponent = (
    <Flex direction="row" justify="space-between" alignItems="flex-end" mt={1} mb={5}>
      {/* NOTE: This Flex is necessary for the styling */}
      <Box alignItems="flex-end">
        <HStack justifyContent="flex-end" alignItems="center">
          <NBText fontSize="sm" fontWeight="medium" color="function.50">
            {formatNumberAsWholeNumber(actualValue)}
          </NBText>
          <NBText fontSize="sm" fontWeight="medium" color="function.30">
            {showTarget ? `/${formatNumberAsWholeNumber(targetValue)} ${t("general.kcal")}` : ` ${t("general.kcal")}`}
          </NBText>
        </HStack>
      </Box>
    </Flex>
  );

  const sortedCalendarItems = _.sortBy(calendarItems, [(item) => (item.content_type === "recipemeal" ? 0 : 1), "id"]);

  return (
    <VStack py={1.5} px={2} nativeID={`mealSlot-${mealSlotSpecification.meal_moment}`}>
      {!_.isEmpty(calendarItems) ? (
        <>
          {sortedCalendarItems.map((item, index) => (
            <Box key={item.id} mt={index > 0 ? 3 : 0}>
              {renderItemFunction(index, item)}
            </Box>
          ))}

          {organisation && doesOrganisationHaveManagedPlanning(organisation) ? null : (
            <Box mt={4}>
              <Button
                onPress={onPressAddMoreComponent}
                testID={`addMoreButton-${mealSlotSpecification.meal_moment}`}
                bg="primary.300"
                borderRadius={theme.borderRadii.button.default}
                py={3}
                px={5}
                _text={{
                  color: "white",
                  fontWeight: "medium",
                }}
                _pressed={{
                  bg: "primary.400",
                }}
              >
                {t("planner.add_more_button_text")}
              </Button>
            </Box>
          )}
        </>
      ) : (
        planMealComponent
      )}
      {deleteMealComponent}
    </VStack>
  );
};

export default MealSlotInPlanner;
